import { apiClient } from './client';
import { parseApiError } from './errors';
import { sumGateBreakdownNumbers } from '../utils/gateAmounts';
import {
  sumKpBreakdownNumbers,
  sumUnitedBreakdownNumbers,
} from '../utils/windowsAmounts';
import type { WizardState } from '../types/wizard.types';

/**
 * Преобразует данные из формы (strings) в серверный формат (numbers).
 * Все суммы — числа, даты — ISO.
 */
function toNumber(s: string): number {
  const cleaned = s.replace(/\s/g, '').replace(',', '.');
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
}

function buildGateFields(gate: WizardState['gate']) {
  const productAmount = toNumber(gate.productAmount);
  const preparationMaterial = toNumber(gate.preparationMaterial);
  const deliveryAmount = toNumber(gate.deliveryAmount);
  const installationAmount = toNumber(gate.installationAmount);
  const preparationWork = toNumber(gate.preparationWork);
  const demontageAmount = toNumber(gate.demontageAmount);
  const totalAmount = sumGateBreakdownNumbers({
    productAmount,
    preparationMaterial,
    deliveryAmount,
    installationAmount,
    preparationWork,
    demontageAmount,
  });

  return {
    ...gate,
    productAmount,
    preparationMaterial,
    deliveryAmount,
    installationAmount,
    preparationWork,
    demontageAmount,
    totalAmount,
    prepaymentAmount: toNumber(gate.prepaymentAmount),
  };
}
function buildWindowsFields(windows: WizardState['windows'], united: boolean) {
  const { podryadPrepaymentMode, podryadPrepaymentAmount, ...commonWindows } = windows;
  const constructionsAmount = toNumber(windows.constructionsAmount);
  const garbageRemovalAmount = toNumber(windows.garbageRemovalAmount);
  const slopesAmount = toNumber(windows.slopesAmount);
  const deliveryAmount = toNumber(windows.deliveryAmount);
  const workDemontage = toNumber(windows.workDemontage);
  const workInstallation = toNumber(windows.workInstallation);
  const workSlopes = toNumber(windows.workSlopes);
  const workFlashings = toNumber(windows.workFlashings);
  const workAdditional = toNumber(windows.workAdditional);
  const totalAmount = united
    ? sumUnitedBreakdownNumbers({
        constructionsAmount,
        slopesAmount,
        deliveryAmount,
        workInstallation,
        workSlopes,
        workAdditional,
      })
    : sumKpBreakdownNumbers({
        constructionsAmount,
        garbageRemovalAmount,
      });

  return {
    ...commonWindows,
    ...(!united ? {
      podryadPrepaymentMode,
      ...(podryadPrepaymentMode === 'custom'
        ? { podryadPrepaymentAmount: toNumber(podryadPrepaymentAmount) }
        : {}),
    } : {}),
    totalAmount,
    constructionsAmount,
    garbageRemovalAmount,
    slopesAmount,
    deliveryAmount,
    prepaymentAmount: toNumber(windows.prepaymentAmount),
    workDemontage,
    workInstallation,
    workSlopes,
    workFlashings,
    workAdditional,
  };
}

function stripPngPrefix(dataUrl: string): string {
  const comma = dataUrl.indexOf(',');
  return comma === -1 ? dataUrl : dataUrl.slice(comma + 1);
}

function normalizePhone(p: string): string {
  const digits = p.replace(/\D/g, '');
  return digits.length === 11 ? `+${digits}` : p;
}

export async function apiReserveNumber(): Promise<{ number: number }> {
  const res = await apiClient.post<{ number: number }>('/contract/reserve-number');
  return res.data;
}

export async function apiGetNextNumber(): Promise<{ next: number }> {
  const res = await apiClient.get<{ next: number }>('/contract/next-number');
  return res.data;
}

function buildContractPayload(state: WizardState): Record<string, unknown> {
  if (!state.variant) throw new Error('Variant not selected');

  const client = {
    ...state.client,
    phone: normalizePhone(state.client.phone),
    phoneSecondary: state.client.phoneSecondary
      ? normalizePhone(state.client.phoneSecondary)
      : undefined,
    actualAddress: state.client.actualAddress || undefined,
  };

  const inserts = state.inserts.map((f) => ({ fileBase64: f.base64, fileName: f.name }));
  if (inserts.length === 0) {
    throw new Error('Прикрепите хотя бы один файл (расчёт/спецификацию)');
  }
  const insertParts = { inserts };

  const isOffice = state.variant !== 'variant3' && state.signingPersonId === 'office';
  const employeeSig = !isOffice && state.signatures.employee
    ? stripPngPrefix(state.signatures.employee)
    : undefined;
  const employeePodryadSig = !isOffice && state.signatures.employeePodryad
    ? stripPngPrefix(state.signatures.employeePodryad)
    : undefined;

  if (state.variant === 'variant3') {
    // Только ворота: передаём gate + 1 подпись
    return {
      variant: 'variant3',
      gateSigningPersonId: state.gateSigningPersonId,
      client,
      gate: buildGateFields(state.gate),
      signatures: {
        employee: employeeSig,
        gate: stripPngPrefix(state.signatures.gate ?? ''),
      },
      ...insertParts,
    };
  }

  const windows = buildWindowsFields(state.windows, state.variant === 'variant1');

  if (state.variant === 'variant1') {
    // Объединённый: только окна + 1 подпись
    return {
      variant: 'variant1',
      signingPersonId: state.signingPersonId,
      client,
      windows,
      signatures: {
        ...(employeeSig ? { employee: employeeSig } : {}),
        windowsKp: stripPngPrefix(state.signatures.windowsKp ?? ''),
      },
      ...insertParts,
    };
  }

  // variant2: раздельный — окна + 2 подписи
  return {
    variant: 'variant2',
    signingPersonId: state.signingPersonId,
    client,
    windows,
    signatures: {
      ...(employeeSig ? { employee: employeeSig } : {}),
      ...(employeePodryadSig ? { employeePodryad: employeePodryadSig } : {}),
      windowsKp: stripPngPrefix(state.signatures.windowsKp ?? ''),
    },
    ...insertParts,
  };
}

/** Собирает payload для предпросмотра (без подписей). */
function buildPreviewPayload(state: WizardState): Record<string, unknown> {
  if (!state.variant) throw new Error('Variant not selected');

  const client = {
    ...state.client,
    phone: normalizePhone(state.client.phone),
    phoneSecondary: state.client.phoneSecondary
      ? normalizePhone(state.client.phoneSecondary)
      : undefined,
    actualAddress: state.client.actualAddress || undefined,
  };

  if (state.variant === 'variant3') {
    return {
      variant: 'variant3',
      gateSigningPersonId: state.gateSigningPersonId,
      client,
      gate: buildGateFields(state.gate),
    };
  }

  const windows = buildWindowsFields(state.windows, state.variant === 'variant1');

  return {
    variant: state.variant,
    signingPersonId: state.signingPersonId,
    client,
    windows,
  };
}

export interface ContractPreviewPages {
  pages: string[];
  labels: string[];
}

/** Запрашивает у сервера HTML каждого договора, рендеренный из тех же HBS-шаблонов что и PDF. */
export async function apiPreviewContract(state: WizardState): Promise<ContractPreviewPages> {
  const payload = buildPreviewPayload(state);
  const res = await apiClient.post<ContractPreviewPages>('/contract/preview-html', payload);
  return res.data;
}

export interface ContractPdfFile {
  fileName: string;
  pdfBase64: string;
  kind: 'windows-united' | 'windows-kp' | 'windows-podryad' | 'gate';
}

export interface GenerateContractResult {
  ok: boolean;
  files: ContractPdfFile[];
  /** Первый файл — для обратной совместимости */
  fileName: string;
  pdfBase64: string;
  emailSent: boolean;
  emailError?: { message: string; code?: string };
}

export interface ResendEmailResult {
  ok: boolean;
  emailSent: boolean;
  emailError?: { message: string; code?: string };
}

/** Повторная отправка письма по уже сформированным PDF (без пересборки). */
export async function apiResendContractEmail(
  state: WizardState,
  files: ContractPdfFile[],
): Promise<ResendEmailResult> {
  try {
    const contract = buildContractPayload(state);
    const res = await apiClient.post<ResendEmailResult>('/contract/resend-email', {
      contract,
      files,
    });
    return res.data;
  } catch (err) {
    throw parseApiError(err);
  }
}

export async function apiGenerateContract(state: WizardState): Promise<GenerateContractResult> {
  try {
    const payload = buildContractPayload(state);
    const res = await apiClient.post<GenerateContractResult>(
      '/contract/generate',
      payload,
    );
    return res.data;
  } catch (err) {
    throw parseApiError(err);
  }
}

export function pdfBase64ToBlob(base64: string): Blob {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: 'application/pdf' });
}

export async function apiDownloadContract(state: WizardState): Promise<Blob> {
  const payload = buildContractPayload(state);
  const res = await apiClient.post<Blob>('/contract/download', payload, {
    responseType: 'blob',
  });
  return res.data;
}
